import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const DEFAULT_HORN = {
    id: null,
    name: '',
    type: 'hikvision',
    ip: '',
    port: 80,
    scheme: 'http',
    username: '',
    password: '',
    enabled: true,
    channel: 1,
    volume: 30,
    mic_volume: 80,
    playback_mode: 'auto',
    verify_tls: true,
    two_way: false,
};

const normalizeHorn = (entry = {}) => {
    const identifier = Number.isFinite(Number(entry?.id)) ? Number.parseInt(entry.id, 10) : null;
    const name = `${entry?.name ?? ''}`.trim();

    return {
        ...DEFAULT_HORN,
        ...entry,
        id: identifier,
        name: name || (identifier !== null ? `Horn ${identifier}` : ''),
        type: `${entry?.type ?? DEFAULT_HORN.type}`.trim() || DEFAULT_HORN.type,
        ip: `${entry?.ip ?? ''}`.trim(),
        port: Number.isFinite(Number(entry?.port)) ? Number.parseInt(entry.port, 10) : DEFAULT_HORN.port,
        scheme: `${entry?.scheme ?? DEFAULT_HORN.scheme}`.trim() || DEFAULT_HORN.scheme,
        username: `${entry?.username ?? ''}`.trim(),
        password: `${entry?.password ?? ''}`,
        enabled: entry?.enabled !== false,
        channel: Number.isFinite(Number(entry?.channel)) ? Math.max(1, Number.parseInt(entry.channel, 10)) : DEFAULT_HORN.channel,
        volume: Number.isFinite(Number(entry?.volume)) ? Math.min(100, Math.max(0, Number.parseInt(entry.volume, 10))) : DEFAULT_HORN.volume,
        mic_volume: Number.isFinite(Number(entry?.mic_volume)) ? Math.min(100, Math.max(0, Number.parseInt(entry.mic_volume, 10))) : DEFAULT_HORN.mic_volume,
        playback_mode: `${entry?.playback_mode ?? DEFAULT_HORN.playback_mode}`.trim() || DEFAULT_HORN.playback_mode,
        verify_tls: entry?.verify_tls !== false,
        two_way: entry?.two_way === true,
    };
};

const HornsSettings = ({items = [], clientTwoWayAudio = false}) => {
    const [list, setList] = useState(Array.isArray(items) ? items.map(normalizeHorn) : []);
    const [open, setOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [horn, setHorn] = useState(DEFAULT_HORN);
    const [dialogError, setDialogError] = useState('');

    const typeOptions = useMemo(() => ['hikvision'], []);
    const schemeOptions = useMemo(() => ['http', 'https'], []);

    useEffect(() => {
        if (!Array.isArray(items)) {
            try {
                const parsed = JSON.parse(items);
                if (Array.isArray(parsed)) {
                    setList(parsed.map(normalizeHorn));
                }
            } catch (error) {
                setList([]);
            }
            return;
        }

        setList(items.map(normalizeHorn));
    }, [items]);

    useEffect(() => {
        const hidden = document.getElementById('horns_config');
        if (!hidden) {
            return;
        }

        try {
            hidden.value = JSON.stringify(list);
            hidden.dispatchEvent(new Event('input', {bubbles: true}));
            hidden.dispatchEvent(new Event('change', {bubbles: true}));
        } catch (error) {
            console.error('Failed to update horns_config hidden input', error);
        }
    }, [list]);

    const resetDialog = () => {
        setEditingIndex(-1);
        setHorn(DEFAULT_HORN);
        setDialogError('');
    };

    const openAdd = () => {
        resetDialog();
        setOpen(true);
    };

    const openEdit = (index) => {
        setEditingIndex(index);
        setHorn(normalizeHorn(list[index] || DEFAULT_HORN));
        setDialogError('');
        setOpen(true);
    };

    const updateHornField = (field, value) => {
        setHorn((previous) => ({
            ...previous,
            [field]: value,
        }));
    };

    const handleSave = () => {
        const normalized = normalizeHorn(horn);
        if (!normalized.ip || !normalized.username || !normalized.password) {
            setDialogError('IP, port, username, and password are required.');
            return;
        }

        if (!Number.isFinite(Number(normalized.port)) || normalized.port < 1 || normalized.port > 65535) {
            setDialogError('Port must be between 1 and 65535.');
            return;
        }

        setList((previous) => {
            const next = [...previous];
            if (editingIndex >= 0) {
                next[editingIndex] = normalized;
            } else {
                next.push(normalized);
            }
            return next;
        });

        setOpen(false);
        resetDialog();
    };

    const handleDelete = (index) => {
        if (!window.confirm('Delete this horn?')) {
            return;
        }

        setList((previous) => previous.filter((_, currentIndex) => currentIndex !== index));
    };

    return (
        <Box>
            <Typography variant="subtitle1" gutterBottom>Horns</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Required fields: IP, port, username, and password. Horn IDs are assigned automatically from the database.
            </Typography>
            <Box sx={{mb: 1}}>
                <Button startIcon={<AddIcon/>} variant="outlined" onClick={openAdd} size="small">Add horn</Button>
            </Box>
            <List dense>
                {list.length === 0 && (
                    <ListItem>
                        <ListItemText primary="No horns configured" />
                    </ListItem>
                )}
                {list.map((entry, index) => (
                    <ListItem key={`${entry.id ?? 'new'}-${index}`} divider alignItems="flex-start">
                        <ListItemText
                            primary={`${entry.name || `Horn ${entry.id ?? index + 1}`}${entry.id ? ` (#${entry.id})` : ''}`}
                            secondary={`${entry.ip}:${entry.port} • ${entry.username} • ${entry.enabled ? 'enabled' : 'disabled'}`}
                        />
                        <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="edit" onClick={() => openEdit(index)} size="small">
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(index)} size="small">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingIndex >= 0 ? 'Edit horn' : 'Add horn'}</DialogTitle>
                <DialogContent>
                    {dialogError && (
                        <Alert severity="error" sx={{mt: 1}}>
                            {dialogError}
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{mt: 0.5}}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Name"
                                type="text"
                                fullWidth
                                value={horn.name}
                                onChange={(event) => updateHornField('name', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="dense"
                                label="IP"
                                type="text"
                                fullWidth
                                required
                                value={horn.ip}
                                onChange={(event) => updateHornField('ip', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="dense"
                                label="Port"
                                type="number"
                                fullWidth
                                required
                                value={horn.port}
                                onChange={(event) => updateHornField('port', event.target.value)}
                                inputProps={{min: 1, max: 65535}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="dense"
                                label="Username"
                                type="text"
                                fullWidth
                                required
                                value={horn.username}
                                onChange={(event) => updateHornField('username', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="dense"
                                label="Password"
                                type="text"
                                fullWidth
                                required
                                value={horn.password}
                                onChange={(event) => updateHornField('password', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="dense">
                                <InputLabel>Type</InputLabel>
                                <Select
                                    label="Type"
                                    value={horn.type}
                                    onChange={(event) => updateHornField('type', event.target.value)}
                                >
                                    {typeOptions.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="dense">
                                <InputLabel>Scheme</InputLabel>
                                <Select
                                    label="Scheme"
                                    value={horn.scheme}
                                    onChange={(event) => updateHornField('scheme', event.target.value)}
                                >
                                    {schemeOptions.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                margin="dense"
                                label="Channel"
                                type="number"
                                fullWidth
                                value={horn.channel}
                                onChange={(event) => updateHornField('channel', event.target.value)}
                                inputProps={{min: 1}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                margin="dense"
                                label="Volume (speaker)"
                                type="number"
                                fullWidth
                                value={horn.volume}
                                onChange={(event) => updateHornField('volume', event.target.value)}
                                inputProps={{min: 0, max: 100}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                margin="dense"
                                label="Mic Volume"
                                type="number"
                                fullWidth
                                value={horn.mic_volume}
                                onChange={(event) => updateHornField('mic_volume', event.target.value)}
                                inputProps={{min: 0, max: 100}}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                margin="dense"
                                label="Playback mode"
                                type="text"
                                fullWidth
                                value={horn.playback_mode}
                                onChange={(event) => updateHornField('playback_mode', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={horn.enabled}
                                        onChange={(event) => updateHornField('enabled', event.target.checked)}
                                    />
                                }
                                label="Enabled"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={horn.verify_tls}
                                        onChange={(event) => updateHornField('verify_tls', event.target.checked)}
                                    />
                                }
                                label="Verify TLS"
                            />
                        </Grid>
                        {clientTwoWayAudio && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={horn.two_way}
                                            onChange={(event) => updateHornField('two_way', event.target.checked)}
                                        />
                                    }
                                    label="Two Way Audio (browser can listen to horn)"
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HornsSettings;
